import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { departmentsTooltip } from "../../../constants/TooltipInformation";
import {
  validateString,
  validateSpace,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const Department = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [departmentName, setDepartmentName] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [departmentInformation, setDepartmentInformation] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const departmentPayload = {
    name: departmentName,
  };

  const addDepartment = () => {
    if (validateInput()) {
      if (!departmentName || departmentName === null || departmentName === "") {
        setNameError(true);
      } else if (
        checkDuplicate(
          Array.isArray(departmentInformation) ? departmentInformation : [],
          DNDFieldsEnum.DEPARTMENTCODE,
          departmentName,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setDepartmentInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...departmentPayload,
                  },
                ]
              : [{ id: 1, ...departmentPayload }],
          );
        });
        setLastUpdatedRecord(departmentPayload);
        editDepartment(null);
        setEditClicked(false);
      }
    }
  };

  const editDepartment = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setDepartmentName(list?.name);
  };

  const updateDepartment = () => {
    if (!validateInput()) return;
    if (!departmentName || departmentName === null || departmentName === "") {
      setNameError(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(departmentInformation)
          ? departmentInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.DEPARTMENTCODE,
        departmentName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setDepartmentInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...departmentPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editDepartment(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.department} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteDepartment = (id: number) => {
    setDepartmentInformation(deleteItem(departmentInformation, id));
  };

  const validateInput = () => {
    setNameError(false);
    if (
      departmentName &&
      !validateSpace(
        departmentName,
        validationLength.departmentName,
        validationType.OTHER,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    data(departmentInformation);
    editClicked &&
      setSelectedId(
        checkData(departmentInformation, selectedId) ? selectedId : null,
      );
  }, [departmentInformation]);

  useEffect(() => {
    fieldValues &&
      setDepartmentInformation(
        Array.isArray(fieldValues)
          ? fieldValues
          : fieldValues
            ? [fieldValues]
            : null,
      );
  }, []);

  useEffect(() => {
    validateInput();
  }, [departmentName]);

  const departmentNameHelperText = nameError
    ? !(departmentName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.department.departmentName
    : versaFieldHelperText.department.departmentName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Department Information
              <Tooltip title={tooltipComponent(departmentsTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editDepartment(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editDepartment(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Department Name
              <Tooltip
                title={tooltipComponent(departmentsTooltip.departmentName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="departmentName"
              variant="outlined"
              placeholder="Department Name"
              helperText={departmentNameHelperText}
              value={departmentName ?? ""}
              sx={{ width: "100%" }}
              error={nameError}
              onChange={(e: any) => {
                setDepartmentName(e.target.value.toUpperCase());
                setNameError(false);
              }}
            />
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateDepartment : addDepartment}
              id="addNewDepartment"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Department
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Room Department Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {departmentInformation ? (
                <DraggableItemList
                  itemData={departmentInformation}
                  onOrderUpdate={(order) => setDepartmentInformation(order)}
                  onEdit={(info) => {
                    editDepartment(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteDepartment(id)}
                  code={DNDFieldsEnum.DEPARTMENTCODE}
                  name={""}
                />
              ) : (
                <Typography variant="body2">
                  No Department Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Department;
